import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Stack, Typography, Alert, CircularProgress, Tabs, Tab, Box, Divider, Tooltip } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetItemUsageStatsByGroupQuery } from '../../../../api/organizationSlice';
import { useGetItemQuery } from '../../../../api/itemDetailSlice';
import { periodToDateRange } from './useChartData';
import ItemImageThumbnails from './ItemImageThumbnails';
import TimeRangeControls from './TimeRangeControls';
import HospitalMultiSelect from './HospitalMultiSelect';
import CardCasePieChart from './CardCasePieChart';
import CardCaseInsights from './CardCaseInsights';
import UsageChartInventory from './UsageChartInventory';
import UsageChartPicklist from './UsageChartPicklist';
import UsageChartForecast from './UsageChartForecast';
import MetricCardList from './MetricCardList';
import { NumericFormat } from 'react-number-format';
import { theme } from '../../../../theme';
import { normalizeGroupData } from './dataGroupingUtils';

export const CHART_VIEWS = {
    INVENTORY_VIEW: 'inventory',
    PICKLIST_VIEW: 'picklist',
    FORECAST_VIEW: 'forecast',
};

export const CARD_TYPES = {
    RUN: 'run',
    RESTOCK: 'restock',
    OPEN_NOT_USED: 'open not used',
    PICKED: 'picked',
    USED: 'used'
};

function ItemAnalytics() {
    const { organizationId, itemId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: itemData, isLoading: isLoadingItem, error: itemError } = useGetItemQuery(itemId);
    const [chartView, setChartView] = useState(searchParams.get('chartView') || CHART_VIEWS.INVENTORY_VIEW);
    const [period, setPeriod] = useState(searchParams.get('period') || null);
    const [dateRange, setDateRange] = useState(searchParams.get('startDate') && searchParams.get('endDate') ? {
        startDate: searchParams.get('startDate'),
        endDate: searchParams.get('endDate')
    } : null);
    const [hospitals, setHospitals] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    
    // Chart control states
    const [chartGroupBy, setChartGroupBy] = useState('card');
    const [chartSelectedActivity, setChartSelectedActivity] = useState(() => {
        const initialChartView = searchParams.get('chartView') || CHART_VIEWS.INVENTORY_VIEW;
        return initialChartView === CHART_VIEWS.PICKLIST_VIEW ? 'run' : 'picked';
    });

    useEffect(() => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);

            if (chartView) {
                newParams.set('chartView', chartView);
            }
            if (chartView === CHART_VIEWS.FORECAST_VIEW) {
                newParams.delete('startDate');
                newParams.delete('endDate');
                newParams.delete('period');
                return newParams;
            }
            if (dateRange?.startDate && dateRange?.endDate) {
                newParams.set('startDate', dateRange.startDate);
                newParams.set('endDate', dateRange.endDate);
            }
            if (period) {
                newParams.set('period', period);
            }

            return newParams;
        }, { replace: true });
    }, [dateRange, period, chartView, setSearchParams]);
    
    // Reset chart controls when chart view changes
    useEffect(() => {
        if (chartView === CHART_VIEWS.PICKLIST_VIEW) {
            setChartSelectedActivity('run');
        } else {
            setChartSelectedActivity('picked');
        }
    }, [chartView]);
    
    // Transform itemData to convert cost from cents to dollars
    const transformedItemData = useMemo(() => {
        if (!itemData) return null;
        return {
            ...itemData,
            cost: itemData.cost ? parseFloat((itemData.cost / 100).toFixed(2)) : null
        };
    }, [itemData]);
    
    const hospitalIds = hospitals.length > 0 ? hospitals.join(',') : undefined;

    const selectedDateRange = useMemo(() => {
        if (!selectedPeriod || !period) return dateRange;
        return periodToDateRange(selectedPeriod, period);
    }, [selectedPeriod, period, dateRange]);

    const groupQuerySkip = !organizationId || !itemId || !selectedDateRange;
    const groupQueryArgs = {
        organizationId,
        itemId,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate,
        hospitalIds
    };

    // Fetch group breakdown data for insights panel
    const { data: timeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'timeSeries', startDate: dateRange?.startDate, endDate: dateRange?.endDate, granularity: period }, { skip: groupQuerySkip || !period });
    const { data: timeRangeItemSummary, isLoading: isLoadingSummary, error: timeRangeItemSummaryError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'summary', startDate: dateRange?.startDate, endDate: dateRange?.endDate }, { skip: groupQuerySkip });
    const { data: cardGroupData, isLoading: isLoadingCard, error: cardGroupError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'card' }, { skip: groupQuerySkip });
    const { data: hospitalGroupData, isLoading: isLoadingHospital, error: hospitalGroupError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'hospital' }, { skip: groupQuerySkip });
    const { data: physicianGroupData, isLoading: isLoadingPhysician, error: physicianGroupError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'physician' }, { skip: groupQuerySkip });
    const { data: procedureGroupData, isLoading: isLoadingProcedure, error: procedureGroupError } =
        useGetItemUsageStatsByGroupQuery({ ...groupQueryArgs, groupBy: 'procedure' }, { skip: groupQuerySkip });

    const isLoadingGroups = isLoadingCard || isLoadingHospital || isLoadingPhysician || isLoadingProcedure || isLoadingTimeSeries || isLoadingSummary;
    const groupLoadingError = cardGroupError || hospitalGroupError || physicianGroupError || procedureGroupError || timeSeriesError || timeRangeItemSummaryError;

    const handleChartViewChange = useCallback((event, newView) => {
        if (newView !== null) {
            setChartView(newView);
        }
    }, []);

    const handlePeriodChange = useCallback((newPeriod) => {
        setPeriod(newPeriod);
    }, []);

    const handleDateRangeChange = useCallback((newDateRange) => {
        setDateRange(newDateRange);
    }, []);

    const handleHospitalChange = useCallback((hospitals) => {
        setHospitals(hospitals);
    }, []);
    
    const handleInsightClick = useCallback((insight) => {
        // Set group by to match the insight category
        setChartGroupBy(insight.section);
        setChartSelectedActivity(insight.activityType);
    }, [chartView]);

    // Build groupingMaps from pre-grouped API responses, normalising each group's
    // id/name fields and computing picked_quantity (open + hold) for createInsights.
    const groupingMaps = useMemo(() => {
        if (!cardGroupData && !hospitalGroupData && !physicianGroupData && !procedureGroupData) return null;
        return {
            card:        normalizeGroupData(cardGroupData,      'card_id',      'card_name'),
            hospital:    normalizeGroupData(hospitalGroupData,  'hospital_id',  'hospital_name'),
            physician:   normalizeGroupData(physicianGroupData, 'physician_id', 'physician_name'),
            procedure:   normalizeGroupData(procedureGroupData, 'procedure_id', 'procedure_name')
        };
    }, [cardGroupData, hospitalGroupData, physicianGroupData, procedureGroupData]);

    return (
        <Stack pb={3} spacing={3}>
            <Stack spacing={1} sx={{ position: 'sticky', top: 64, zIndex: 5, backgroundColor: theme.palette.background.white }}>
                <Stack px={3} direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack spacing={1} maxWidth='35%'>
                        <Tooltip title={transformedItemData?.name || 'Item Name'} placement="top" arrow>
                            <Typography variant='heavy' fontSize='20px' sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                Usage Analytics for: <strong>{transformedItemData?.name}</strong>
                            </Typography>
                        </Tooltip>
                        <Stack direction='row' spacing={2} alignItems='center' divider={<Divider orientation='vertical' flexItem />}>
                            <Typography variant="body2" color="text.secondary" fontSize='16px'>
                                SKU: {transformedItemData?.vendor.name} - {transformedItemData?.part_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontSize='16px'>
                                Unit Cost: <NumericFormat
                                    value={transformedItemData?.cost}
                                    thousandSeparator={true}
                                    displayType={'text'}
                                    prefix={'$'}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                /> 
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction='row' spacing={2} alignItems="flex-start" width='60%'>
                        <TimeRangeControls
                            periodIn={period}
                            onPeriodChange={handlePeriodChange}
                            dateRangeIn={dateRange}
                            onDateRangeChange={handleDateRangeChange}
                            chartView={chartView}
                        />
                        
                        <HospitalMultiSelect
                            organizationId={organizationId}
                            onHospitalChange={handleHospitalChange}
                        />
                    </Stack>
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={chartView} 
                        onChange={handleChartViewChange}
                        aria-label="chart view tabs"
                    >
                        <Tab 
                            label="Inventory Trends" 
                            value={CHART_VIEWS.INVENTORY_VIEW} 
                        />
                        <Tab 
                            label="Case Picking Insights" 
                            value={CHART_VIEWS.PICKLIST_VIEW} 
                        />
                        <Tab 
                            label="Usage Forecast" 
                            value={CHART_VIEWS.FORECAST_VIEW} 
                        />
                    </Tabs>
                </Box>
            </Stack>
            
            {isLoadingItem && (
                <Stack spacing={2} alignItems="center" py={4}>
                    <CircularProgress />
                    <Typography>Loading item details...</Typography>
                </Stack>
            )}
            
            {itemError && (
                <Alert severity="error">
                    Error loading item: {itemError.message || 'Item not found'}
                </Alert>
            )}
            
            {transformedItemData && (
                <Stack px={3}>
                    <Stack spacing={2} direction="row" alignItems="flex-start">
                        {transformedItemData.images && transformedItemData.images.length > 0 && (
                            <ItemImageThumbnails 
                                images={transformedItemData.images} 
                                maxDisplay={1} 
                                size={300}
                            />
                        )}
                    </Stack>
                    
                    <Stack spacing={2}>
                        {isLoadingGroups && (
                            <Stack spacing={2} alignItems="center" py={4}>
                                <CircularProgress />
                                <Typography>Loading usage data...</Typography>
                            </Stack>
                        )}
                        
                        {groupLoadingError && (
                            <Alert severity="error">
                                Error loading usage data: {groupLoadingError.message || 'Please try again'}
                            </Alert>
                        )}
                        
                        {groupingMaps && timeRangeItemSummary && (
                            <Stack spacing={3}>
                                <Box sx={{ marginLeft: '80px', marginRight: '130px' }}>
                                    <MetricCardList
                                        summaryData={timeRangeItemSummary}
                                        chartView={chartView === CHART_VIEWS.FORECAST_VIEW ? CHART_VIEWS.INVENTORY_VIEW : chartView}
                                        dateRange={dateRange}
                                        period={period}
                                    />
                                </Box>
                                {chartView === CHART_VIEWS.INVENTORY_VIEW ? (
                                    <UsageChartInventory
                                        timeSeriesData={timeSeriesData}
                                        period={period}
                                        itemData={transformedItemData}
                                        dateRange={dateRange}
                                        selectedPeriod={selectedPeriod}
                                        setSelectedPeriod={setSelectedPeriod}
                                    />
                                ) : chartView === CHART_VIEWS.PICKLIST_VIEW ? (
                                    <UsageChartPicklist
                                        timeSeriesData={timeSeriesData}
                                        period={period}
                                        itemData={transformedItemData}
                                        dateRange={dateRange}
                                        selectedPeriod={selectedPeriod}
                                        setSelectedPeriod={setSelectedPeriod}
                                    />
                                ) : (
                                    <UsageChartForecast
                                        timeSeriesData={timeSeriesData}
                                        period={period}
                                        itemData={transformedItemData}
                                        forecastDateRange={dateRange || { startDate: null, endDate: null }}
                                        selectedPeriod={selectedPeriod}
                                        setSelectedPeriod={setSelectedPeriod}
                                    />
                                )}

                                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1 }}>
                                        <CardCaseInsights
                                            groupingMaps={groupingMaps}
                                            chartView={chartView}
                                            isLoading={isLoadingGroups}
                                            summaryError={groupLoadingError}
                                            onInsightClick={handleInsightClick}
                                        /> 
                                    </Box>
                                    
                                    <Box sx={{ flex: 1 }}>
                                        <CardCasePieChart
                                            chartView={chartView}
                                            groupingMaps={groupingMaps}
                                            isLoading={isLoadingGroups}
                                            error={groupLoadingError}
                                            groupBy={chartGroupBy}
                                            selectedActivity={chartSelectedActivity}
                                            onGroupByChange={setChartGroupBy}
                                            onActivityChange={setChartSelectedActivity}
                                        />
                                    </Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default ItemAnalytics;